import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import useCountries from '@afosto/utils-react/useCountries';
import useForm from '@afosto/utils-react/validation/useForm';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import FormLabel from '@material-ui/core/FormLabel';
import Form from '@afosto/app-components-react/Form';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import RegisterFormProvider from '@afosto/app-components-react/RegisterFormProvider';
import SchemaForm from '@afosto/app-components-react/SchemaForm';
import TextField from '@afosto/app-components-react/TextField';
import isDefined from '@afosto/utils/isDefined';
import getConfiguration from './getConfiguration';
import formatConfiguration from './formatConfiguration';
import { getUISchema } from './getUISchema';
import translations from './translations';

const IntegrationForm = ({ app, integration, isLoading, onCancel, onSubmit }) => {
  const intl = useIntl();
  const countries = useCountries();
  const configurationFormRef = useRef(undefined);
  const credentialsFormRef = useRef(undefined);
  const configuration = getConfiguration(integration, app);

  const form = useForm({
    defaultValues: {
      name: integration?.name,
      credentials: {},
      configuration,
    },
    onSubmit: async data => {
      try {
        const { current: configurationForm } = configurationFormRef;
        const { current: credentialsForm } = credentialsFormRef;
        let configurationValues = {};
        let credentialValues = {};

        if (configurationForm) {
          configurationForm.onSubmit();

          const configurationIsValid = await configurationForm.isValid();

          if (!configurationIsValid) {
            return;
          }

          configurationValues = configurationForm.getValues({ nest: true });
          configurationValues = formatConfiguration(app, configurationValues);
        }

        if (credentialsForm) {
          const currentValues = credentialsForm.getValues({ nest: true });
          const hasAtLeastOneValue = Object.keys(currentValues).some(key =>
            isDefined(currentValues[key]),
          );

          if (!integration || (integration && hasAtLeastOneValue)) {
            credentialsForm.onSubmit();

            const credentialsIsValid = await credentialsForm.isValid();

            if (!credentialsIsValid) {
              return;
            }

            credentialValues = credentialsForm.getValues({ nest: true });
          }
        }

        if (onSubmit && typeof onSubmit === 'function') {
          onSubmit({ ...data, configuration: configurationValues, credentials: credentialValues });
        }
      } catch (err) {
        // Do nothing.
      }
    },
    onCancel,
  });

  const credentialsSchema = isDefined(app?.authentication?.schema)
    ? JSON.parse(atob(app.authentication.schema))
    : null;
  const showCredentialsForm = !!credentialsSchema;

  const configurationSchema = isDefined(app?.config_schema)
    ? JSON.parse(atob(app.config_schema))
    : null;

  const uiSchemas = getUISchema(intl, app?.code, {
    countries: countries.map(({ label, value }) => ({ label, value: value?.toLowerCase() })),
  });

  return (
    <LoadingWrapper isLoading={form.isSubmitting || isLoading}>
      <Form form={form}>
        <TextField name="name" label={intl.formatMessage(translations.fields.name.label)} />

        {showCredentialsForm && (
          <RegisterFormProvider value={{ formRef: credentialsFormRef }}>
            <Box mt={2}>
              <FormLabel component="legend" style={{ marginBottom: 4 }}>
                <FormattedMessage {...translations.fields.credentials.label} />
              </FormLabel>
              <SchemaForm
                schema={credentialsSchema}
                uiSchema={uiSchemas.credentials ?? {}}
                renderActions={false}
              />
            </Box>
          </RegisterFormProvider>
        )}

        {!!configurationSchema && (
          <RegisterFormProvider value={{ formRef: configurationFormRef }}>
            <Box mt={2}>
              <FormLabel component="legend" style={{ marginBottom: 4 }}>
                <FormattedMessage {...translations.fields.configuration.label} />
              </FormLabel>
              <SchemaForm
                schema={configurationSchema}
                uiSchema={uiSchemas.configuration ?? {}}
                values={configuration}
                renderActions={false}
              />
            </Box>
          </RegisterFormProvider>
        )}
      </Form>
    </LoadingWrapper>
  );
};

IntegrationForm.propTypes = {
  app: PropTypes.object,
  integration: PropTypes.object,
  isLoading: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
};

IntegrationForm.defaultProps = {
  app: undefined,
  integration: undefined,
  isLoading: false,
  onCancel: undefined,
  onSubmit: undefined,
};

export default IntegrationForm;
